import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FeatherPipe } from '../../../core/pipes/feather.pipe';
import { ButtonComponent } from '../button/button.component';
import { TooltipDirective } from '../../directives';
import { ModalComponent } from '../modal/modal.component';
import {
  WorkflowUserApproverModel,
  WorkflowUserApproverUser,
} from '../../../core/models/workflow/workflow-order.model';
import { WorkflowOrderService } from '../../../core/services/workflow-order/workflow-order.service';

/** Maps API feedback (0/1/-1) to display status */
function feedbackToStatus(
  feedback: number
): 'awaiting' | 'approved' | 'rejected' {
  if (feedback === 1) return 'approved';
  if (feedback === -1) return 'rejected';
  return 'awaiting';
}

@Component({
  selector: 'app-approval-workflow',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FeatherPipe,
    ButtonComponent,
    TooltipDirective,
    ModalComponent,
  ],
  templateUrl: './approval-workflow.component.html',
})
export class ApprovalWorkflowComponent {
  @Input({ required: true }) workflow!: WorkflowUserApproverModel;
  @Input({ required: true }) referenceId!: string;
  /** Whether the current user has one of allowedRoles and can perform actions */
  @Input() canPerformActions = false;
  @Input() name = 'Aprovação de Workflow';
  @Input() description = '';

  @Output() workflowUpdated = new EventEmitter<WorkflowUserApproverModel | null>();
  @Output() workflowCancelled = new EventEmitter<void>();

  @ViewChild('feedbackModal') feedbackModal!: ModalComponent;

  private readonly workflowService = inject(WorkflowOrderService);

  feedbackMessage = '';
  pendingFeedbackApproved: boolean | null = null;
  loading = false;
  cancelLoading = false;
  recompileLoading = false;

  get statusBadgeClass(): string {
    const w = this.workflow;
    if (w.isApproved) {
      return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
    if (w.isDenied) {
      return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    }
    return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  }

  get statusLabel(): string {
    const w = this.workflow;
    if (w.isApproved) return 'Aprovado';
    if (w.isDenied) return 'Reprovado';
    return 'Aprovação Pendente';
  }

  approverStatus(user: WorkflowUserApproverUser): 'awaiting' | 'approved' | 'rejected' {
    return feedbackToStatus(user.feedback);
  }

  openFeedbackModal(approved: boolean): void {
    if (this.loading || !this.canPerformActions || this.workflow.isFinished)
      return;
    this.pendingFeedbackApproved = approved;
    this.feedbackMessage = '';
    this.feedbackModal.openDialog();
  }

  submitFeedback(): void {
    const msg = (this.feedbackMessage ?? '').trim();
    if (!msg) return;
    if (this.loading || this.pendingFeedbackApproved === null) return;

    this.loading = true;
    this.workflowService
      .feedback(this.workflow.workflowId, this.pendingFeedbackApproved, msg)
      .subscribe({
        next: (updated) => {
          this.loading = false;
          this.feedbackModal.closeDialog();
          this.workflowUpdated.emit(updated);
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  closeFeedbackModal(): void {
    this.feedbackModal.closeDialog();
    this.pendingFeedbackApproved = null;
    this.feedbackMessage = '';
  }

  onCancel(): void {
    if (
      this.cancelLoading ||
      !this.canPerformActions ||
      this.workflow.isFinished
    )
      return;
    this.cancelLoading = true;
    this.workflowService.cancel(this.workflow.workflowId).subscribe({
      next: () => {
        this.cancelLoading = false;
        this.workflowCancelled.emit();
      },
      error: () => {
        this.cancelLoading = false;
      },
    });
  }

  onRecompile(): void {
    if (
      this.recompileLoading ||
      !this.canPerformActions ||
      this.workflow.isFinished
    )
      return;
    this.recompileLoading = true;
    this.workflowService.recompile(this.referenceId).subscribe({
      next: (updated) => {
        this.recompileLoading = false;
        this.workflowUpdated.emit(updated);
      },
      error: () => {
        this.recompileLoading = false;
      },
    });
  }
}
